/**
 * lib/sliceTime.js — Phase E v8c.3.2 · Slice-local ↔ Clip-local time conversion.
 *
 * The `/api/clips/{cid}/pass-vectors/{vid}/frames` endpoint returns
 * `timestamp_s` values that are SLICE-LOCAL (0 … 6 s relative to the
 * start of the 6 s harvested slice). The `/timestamps` endpoint (R/C
 * override) however takes CLIP-LOCAL values (0 … clip_duration_s).
 *
 * Pre-v8c.3.2 the frontend posted the slice-local PTS verbatim, which
 * the backend then interpreted as clip-local — re-cutting the slice
 * to a completely different position. After one round-trip the user
 * saw a totally different frame (typically MUCH later in the clip)
 * marked as the new release point.
 *
 * Both helpers below are PURE — they live in their own module so a
 * Jest unit test can lock in the math without booting React.
 *
 * Convention: by construction in `harvester.py`, frame 0 of every
 * slice lands `pre_release_s` seconds BEFORE the release moment. So
 * slice-local PTS = `pre_release_s` (default 1.0 s) is the release
 * frame and corresponds 1:1 to the persisted clip-local
 * `vector.timestamp_seconds`.
 */

/**
 * Convert a slice-local PTS (0 … slice_duration) to clip-local seconds
 * relative to the parent clip's start.
 *
 * @param {number} slicePts - The frame's slice-local PTS in seconds
 *                             (from frames.json / GET /frames).
 * @param {number} preReleaseS - Slice pre-release pad in seconds
 *                                (= harvester.PRE_RELEASE_S, default 1.0).
 * @param {number} clipAnchor - The vector's persisted clip-local anchor
 *                               (`vector.timestamp_seconds`). Used as the
 *                               reference point because slice-local PTS =
 *                               preReleaseS maps 1:1 to clipAnchor.
 * @returns {number} clip-local seconds.
 */
export function sliceLocalPtsToClipLocal(slicePts, preReleaseS, clipAnchor) {
  // Slice-local PTS = pre_release_s ⇔ clip-local = clipAnchor.
  // Any deviation δ from pre_release_s translates to the SAME deviation
  // in clip-local time (1 s in the slice = 1 s in the clip — they
  // share the same time base, only the origin differs).
  const safeAnchor = Number.isFinite(clipAnchor) ? clipAnchor : 0;
  const safePre = Number.isFinite(preReleaseS) ? preReleaseS : 1.0;
  const safeSlice = Number.isFinite(slicePts) ? slicePts : 0;
  return safeAnchor + (safeSlice - safePre);
}

/**
 * Inverse: clip-local seconds → slice-local PTS. Useful for placing
 * badges (e.g. "release frame is here") on the thumbnail strip.
 */
export function clipLocalToSliceLocalPts(clipT, preReleaseS, clipAnchor) {
  const safeAnchor = Number.isFinite(clipAnchor) ? clipAnchor : 0;
  const safePre = Number.isFinite(preReleaseS) ? preReleaseS : 1.0;
  const safeClip = Number.isFinite(clipT) ? clipT : 0;
  return safePre + (safeClip - safeAnchor);
}
